import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaChevronDown, FaRobot, FaOpenid, FaGoogle, FaCloud, FaRocket } from 'react-icons/fa';

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  border-radius: 3px;
  border: 1px solid #3b424a;
  background-color: #2f343a;
  color: #e0f0ff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #0073e6;
  }

  &:focus {
    border-color: #0073e6;
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 115, 230, 0.1);
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: #2f343a;
  border-radius: 3px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  display: ${props => props.isOpen ? 'block' : 'none'};
  
  /* Customize scrollbar for dark theme */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #1a1d23;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #0073e6;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #0062cc;
  }
`;

const DropdownItem = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e0f0ff;
  
  &:hover {
    background-color: #3b424a;
  }
  
  svg {
    color: ${props => {
      switch(props.provider) {
        case 'openai': return '#10a37f';
        case 'google': return '#4285f4';
        case 'anthropic': return '#b668ff';
        case 'groq': return '#ff6b6b';
        case 'ollama': return '#ff9900';
        default: return '#0073e6';
      }
    }};
  }
`;

const SelectedValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.2s;
  color: #a0c0e0;
`;

const ModelProviderDropdown = ({ value, onChange, required }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const providers = [
    { id: 'openai', name: 'OpenAI', icon: <FaOpenid /> },
    { id: 'google', name: 'Google (Gemini)', icon: <FaGoogle /> },
    { id: 'anthropic', name: 'Anthropic (Claude)', icon: <FaCloud /> },
    { id: 'groq', name: 'Groq', icon: <FaRocket /> },
    { id: 'ollama', name: 'Ollama', icon: <FaRobot /> }
  ];
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  
  const handleSelect = (providerId) => {
    onChange(providerId);
    setIsOpen(false);
  };
  
  const getSelectedProvider = () => {
    return providers.find(p => p.id === value) || providers[0];
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const selectedProvider = getSelectedProvider();
  
  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton 
        type="button" 
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        required={required}
      >
        <SelectedValue>
          {selectedProvider.icon}
          {selectedProvider.name}
        </SelectedValue>
        <ChevronIcon isOpen={isOpen}>
          <FaChevronDown />
        </ChevronIcon>
      </DropdownButton>
      
      <DropdownContent isOpen={isOpen} role="listbox">
        {providers.map(provider => (
          <DropdownItem 
            key={provider.id}
            provider={provider.id}
            onClick={() => handleSelect(provider.id)}
            role="option"
            aria-selected={value === provider.id}
          >
            {provider.icon}
            {provider.name}
          </DropdownItem>
        ))}
      </DropdownContent>
    </DropdownContainer>
  );
};

export default ModelProviderDropdown;
